import React, { useState, useEffect } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import axios from 'axios';
import Classes from './Classes';
import Pending from './Pending';
import Evaluation from './Evaluation';
import './MyClasses.css';

const MyClasses = ({ userData }) => {
  const [currentTab, setCurrentTab] = useState('classes');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState('today');
  const [counts, setCounts] = useState({ pendingFeedbacks: 0, nbOfEvaluations: 0 });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teacher/myclasses/${userData.id}`
        );
        setCounts(response.data);
      } catch (error) {
        console.error('Error fetching counts:', error);
      } finally {
        setLoadingCounts(false);
        setIsLoading(false);
      }
    };
  
    if (userData?.id) {
      fetchCounts();
      
      const intervalId = setInterval(() => {
        fetchCounts();
      }, 60000);
      
      return () => clearInterval(intervalId);
    } else {
      setIsLoading(true);
    }
  }, [userData?.id]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });

  const formattedTime = currentDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  const handleSubmitrefresh = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/teacher/myclasses/${userData.id}`
      );
      setCounts(response.data);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');

  const handleDayChange = (day) => {
    setSelectedDay(day);
  };

  if (isLoading || !userData) {
    return (
      <div className="teacherportal-container">
        <div className="teacherportal-loading">
          <div className="teacherportal-spinner"></div>
          <p>Loading your classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teacherportal-container">
      {/* Header */}
      <header className="teacherportal-header">
        <div className="teacherportal-header-left">
          <h1 className="teacherportal-title">
            <span className="teacherportal-greeting">Hello, </span>
            <span className="teacherportal-teacher-name">{userData.fullName}</span>
          </h1>
          <p className="teacherportal-subtitle">Your Teaching Schedule</p>
        </div>
        <div className="teacherportal-header-right">
          <div className="teacherportal-current-time">
            <span className="teacherportal-time-display">{formattedTime}</span>
            <span className="teacherportal-date-display">{formattedDate}</span>
          </div>
        </div>
      </header>

      {/* Quick Stats */}
      <div className="teacherportal-stats">
        <div className="teacherportal-stat-card">
          <span className="teacherportal-stat-label">Pending Feedback</span>
          <span className="teacherportal-stat-value teacherportal-stat-pending">
            {loadingCounts ? '...' : counts.pendingFeedbacks}
          </span>
        </div>
        <div className="teacherportal-stat-card">
          <span className="teacherportal-stat-label">Evaluations</span>
          <span className="teacherportal-stat-value teacherportal-stat-evaluation">
            {loadingCounts ? '...' : counts.nbOfEvaluations}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <div className="teacherportal-navigation">
        <div className="teacherportal-tabs-container">
          <button
            className={`teacherportal-tab ${currentTab === 'classes' ? 'teacherportal-tab-active' : ''}`}
            onClick={() => setCurrentTab('classes')}
          >
            <span className="teacherportal-tab-text">My Classes</span>
            {currentTab === 'classes' && <div className="teacherportal-tab-indicator"></div>}
          </button>
          
          <button
            className={`teacherportal-tab ${currentTab === 'pending' ? 'teacherportal-tab-active' : ''}`}
            onClick={() => setCurrentTab('pending')}
          >
            <span className="teacherportal-tab-text">Pending Feedback</span>
            {counts.pendingFeedbacks > 0 && (
              <span className="teacherportal-notification-badge">
                {loadingCounts ? '...' : counts.pendingFeedbacks}
              </span>
            )}
            {currentTab === 'pending' && <div className="teacherportal-tab-indicator"></div>}
          </button>
          
          <button
            className={`teacherportal-tab ${currentTab === 'evaluation' ? 'teacherportal-tab-active' : ''}`}
            onClick={() => setCurrentTab('evaluation')}
          >
            <span className="teacherportal-tab-text">Evaluation</span>
            {counts.nbOfEvaluations > 0 && (
              <span className="teacherportal-notification-badge">
                {loadingCounts ? '...' : counts.nbOfEvaluations}
              </span>
            )}
            {currentTab === 'evaluation' && <div className="teacherportal-tab-indicator"></div>}
          </button>
        </div>

        <div className="teacherportal-day-selector-wrapper">
          <div className="teacherportal-day-selector">
            <select
              value={selectedDay}
              onChange={(e) => handleDayChange(e.target.value)}
              className="teacherportal-day-dropdown"
            >
              <option value="yesterday">Yesterday</option>
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
            </select>
            <FiChevronDown className="teacherportal-dropdown-icon" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="teacherportal-content-wrapper">
        {currentTab === 'classes' && <Classes userData={userData} day={selectedDay} />}
        {currentTab === 'pending' && <Pending onSubmitrefresh={handleSubmitrefresh} userData={userData} />}
        {currentTab === 'evaluation' && <Evaluation userData={userData} />}
      </div>
    </div>
  );
};

export default MyClasses;